const MARKETS = [
  {
    code: "01",
    name: "North America",
    detail: "United States & Canada",
    note: "Launch market · Year 1",
  },
  {
    code: "02",
    name: "Latin America",
    detail: "Regional rollout",
    note: "Year 2–3",
  },
  {
    code: "03",
    name: "European Union",
    detail: "27-member bloc",
    note: "Year 3–4",
  },
  {
    code: "04",
    name: "East Africa",
    detail: "Kenya-anchored · East African Community",
    note: "Year 4–5",
  },
];

export default function FrameworkSection() {
  return (
    <section
      id="vision"
      data-testid="vision-section"
      className="relative border-b border-line bg-white"
    >
      <div className="w-full max-w-none px-4 sm:px-8 lg:px-12 py-20 sm:py-28 lg:py-32">
        <div className="flex items-center justify-between mb-14">
          <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-ink-soft">
            06 / The Vision
          </span>
          <span className="hidden sm:block font-mono text-[11px] uppercase tracking-[0.28em] text-ink-soft">
            Women's Economy · Long view
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-6">
            <h2
              data-testid="vision-headline"
              className="font-display font-bold tracking-tight text-ink text-[1.75rem] sm:text-[2rem] lg:text-[2.25rem] leading-[1.2]"
            >
              The female economy is{" "}
              <span className="text-brand-orange">$31.8&nbsp;trillion</span>{" "}
              and counting.
            </h2>

            <div className="mt-10 space-y-6 text-lg sm:text-xl text-ink-soft leading-relaxed max-w-2xl">
              <p>
                Women control roughly{" "}
                <span className="text-ink font-mono">$31.8T</span> in global
                consumer spending. In the United States alone, 14.5 million
                women-owned businesses generate{" "}
                <span className="text-ink font-mono">$3.3T</span> a year — and
                that money typically circulates just once before draining out
                of the ecosystem.
              </p>
              <p>
                SheScan is the consumer layer of a longer thesis: every scan
                is a vote, and enough votes shift where capital flows. The
                vision is to grow internal dollar velocity from{" "}
                <span className="text-ink">1×</span> toward{" "}
                <span className="text-brand-orange font-mono">6–8×</span>{" "}
                across four markets over five years.
              </p>
            </div>
          </div>

          {/* Velocity math card */}
          <div className="lg:col-span-6">
            <div className="border border-line">
              <div className="flex items-center justify-between px-6 py-3 border-b border-line bg-white">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink">
                  Velocity model
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft">
                  WE · 5-year horizon
                </span>
              </div>

              <div className="p-6 sm:p-10 space-y-8">
                {/* Base */}
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft mb-2">
                    Existing base
                  </div>
                  <div className="flex items-baseline justify-between gap-6">
                    <span className="font-display font-bold tracking-tighter text-5xl sm:text-6xl lg:text-7xl text-brand-orange tabular-nums">
                      $3.3T
                    </span>
                    <span className="font-mono text-xs text-ink-soft">
                      @ 1× velocity
                    </span>
                  </div>
                </div>

                {/* Bar */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em]">
                    <span className="text-ink-soft">Current circulation</span>
                    <span className="text-ink">1×</span>
                  </div>
                  <div className="h-2 bg-white border border-line overflow-hidden">
                    <div className="h-full bg-ink" style={{ width: "12.5%" }} />
                  </div>
                  <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em] pt-4">
                    <span className="text-brand-orange">Target circulation</span>
                    <span className="text-brand-orange">6–8×</span>
                  </div>
                  <div className="h-2 bg-white border border-line overflow-hidden">
                    <div
                      className="h-full bg-brand-orange"
                      style={{ width: "87.5%" }}
                    />
                  </div>
                </div>

                {/* Target */}
                <div className="pt-6 border-t border-line">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-orange mb-2">
                    Realized activity — SheScan layer
                  </div>
                  <div className="flex items-baseline justify-between gap-6">
                    <span className="font-mono tracking-tighter text-5xl sm:text-6xl lg:text-7xl text-brand-orange">
                      $19–26T
                    </span>
                    <span className="font-mono text-xs text-ink-soft">
                      @ 6–8× velocity
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Markets grid */}
        <div className="mt-20 border-t border-line">
          <div className="flex items-center justify-between py-4 border-b border-line">
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-ink">
              Rollout · Four markets · Five years
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-ink-soft hidden sm:inline">
              Sequenced · not speculative
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
            {MARKETS.map((m, i) => (
              <div
                key={m.code}
                data-testid={`market-${m.code}`}
                className={`group p-6 sm:p-8 border-line border-b md:border-b-0 ${
                  i < MARKETS.length - 1 ? "md:border-r" : ""
                } hover:border-link transition-colors duration-150`}
              >
                <div className="flex items-start justify-between mb-8">
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft group-hover:text-ink-soft">
                    {m.code}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-orange">
                    {m.note}
                  </span>
                </div>
                <h4 className="font-display font-bold uppercase text-2xl sm:text-3xl tracking-tight text-ink group-hover:text-ink leading-tight">
                  {m.name}
                </h4>
                <p className="mt-3 text-sm text-ink-soft group-hover:text-ink-soft">
                  {m.detail}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
